import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/store';
import type { Branding, MemberRole, PublicTenant } from '../lib/types';
import { platformHost, rememberTenantSlug } from '../lib/config';
import { Card, Notice } from '../components/Shell';
import { TextArea, TextField } from '../components/Field';
import { Stamp } from '../components/Stamp';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

interface CreateTenantResult {
  tenant: {
    tenantId: string;
    slug: string;
    businessName: string;
    branding: Branding;
  };
  membership: { role: MemberRole };
}

/** Turn a business name into the slug the API will accept, for live preview. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/**
 * Set up a new sitting business. A tenant is created with a full starter
 * catalogue and a signable agreement, so the sitter can take a booking the same
 * day rather than authoring nine services and a contract first.
 */
export function Onboarding() {
  const navigate = useNavigate();
  const { user, adoptMembership } = useSession();

  const [businessName, setBusinessName] = useState('');
  const [slug, setSlug] = useState('');
  const [publicEmail, setPublicEmail] = useState(user?.email ?? '');
  const [publicPhone, setPublicPhone] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [about, setAbout] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const effectiveSlug = slug || slugify(businessName);
  const host = platformHost();

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <h1 className="text-3xl">Create an account first</h1>
        <p className="mt-3 text-[color:var(--color-ink-soft)]">
          Setting up a business needs a login, so we know it is yours.
        </p>
        <div className="mt-6 flex gap-3">
          <Link className="btn btn-primary" to="/sign-up">
            Create an account
          </Link>
          <Link className="btn btn-secondary" to="/sign-in">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    void (async () => {
      try {
        const result = await api<CreateTenantResult>('/tenants', {
          method: 'POST',
          body: {
            businessName,
            slug: effectiveSlug,
            publicEmail,
            publicPhone: publicPhone || undefined,
            serviceArea: serviceArea || undefined,
            about: about || undefined,
            timezone,
          },
        });

        rememberTenantSlug(result.tenant.slug);

        // Neither of these goes near an index. The membership is the one the
        // API just wrote, and the tenant is fetched by its id, so a sitter is
        // never bounced off /admin because a secondary index has not caught up
        // with the business they created a second ago.
        const full = await api<{ tenant: PublicTenant }>(`/tenants/${result.tenant.tenantId}`);
        adoptMembership(
          {
            tenantId: result.tenant.tenantId,
            slug: result.tenant.slug,
            businessName: result.tenant.businessName,
            branding: result.tenant.branding,
            role: result.membership.role,
            status: 'active',
          },
          full.tenant
        );

        navigate('/admin', { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          setFieldErrors(err.fieldErrors ?? {});
          setError(err.fieldErrors ? null : err.message);
        } else {
          setError('Something went wrong. Try again.');
        }
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-16">
      <p className="eyebrow mb-3">Set up your business</p>
      <h1 className="text-4xl">Put the binder down</h1>
      <p className="mt-4 max-w-xl text-lg text-[color:var(--color-ink-soft)]">
        Answer six questions and you get a booking page, a client portal, a record for every animal
        in your care, vaccine tracking, and a services agreement people can sign on their phone.
        Then you can go back to the part you actually like.
      </p>

      <ul className="mt-6 flex flex-wrap gap-3">
        <Stamp tone="ok" flat>Nine services ready</Stamp>
        <Stamp tone="pending" flat>Agreement drafted</Stamp>
        <Stamp tone="quiet" flat>Edit everything later</Stamp>
      </ul>

      {error && (
        <div className="mt-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <Card className="mt-8 p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          <TextField
            label="Business name"
            value={businessName}
            onChange={(value) => {
              setBusinessName(value);
              if (!slug) setSlug('');
            }}
            error={fieldErrors.businessName}
            placeholder="Grandma's Critter Lovin"
            required
          />

          <div>
            <TextField
              label="Web address"
              value={slug}
              onChange={(value) => setSlug(slugify(value))}
              error={fieldErrors.slug}
              placeholder={slugify(businessName) || 'your-business'}
            />
            <p className="mt-1.5 font-mono text-xs text-[color:var(--color-ink-faint)]">
              {effectiveSlug || 'your-business'}.{host}
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Contact email"
              type="email"
              value={publicEmail}
              onChange={setPublicEmail}
              error={fieldErrors.publicEmail}
              hint="Shown on your booking page."
              required
            />
            <TextField
              label="Phone"
              type="tel"
              value={publicPhone}
              onChange={setPublicPhone}
              error={fieldErrors.publicPhone}
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Area you cover"
              value={serviceArea}
              onChange={setServiceArea}
              error={fieldErrors.serviceArea}
              placeholder="Round Rock and north Austin"
            />
            <label className="block">
              <span className="field-label">Timezone</span>
              <select className="field-select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone.split('/')[1].replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-[color:var(--color-ink-soft)]">
                Drop-off windows and nightly rates are counted in your local time.
              </span>
            </label>
          </div>

          <TextArea
            label="About your care"
            value={about}
            onChange={setAbout}
            error={fieldErrors.about}
            rows={4}
            placeholder="What makes your care different. This is the first thing an owner reads."
          />

          <button type="submit" className="btn btn-primary w-full" disabled={busy || !businessName}>
            {busy ? 'Setting up' : 'Create my business'}
          </button>
        </form>
      </Card>
    </div>
  );
}
